/* React and hooks */
import React, { useState, useEffect, useCallback } from 'react';

/* API client */
import apiClient from '../../apiClient';

/* Common UI components */
import Button from '../../Component/common/Button/Button';
import Header from '../../Component/common/Header/Header';

/* PO Components */
import PoCreate from '../PO/POCreate'; // reuse if needed
import PoModal from '../PO/POModal';
import PurchaseListTable from '../../Component/PO/purchaseListTable';

/* Styles */
import './po.css';

const PurchaseOrderPage = () => {
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedPO, setSelectedPO] = useState(null);
  const [activeTab, setActiveTab] = useState('All'); // All, COMPLETED, PENDING

  /* ================= FETCH ALL POs ================= */
  const fetchPOs = useCallback(async () => {
    try {
      const response = await apiClient.get('/api/purchase-orders');
      setPurchaseOrders(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching purchase orders:', error);
      setPurchaseOrders([]);
    }
  }, []);

  useEffect(() => {
    fetchPOs();
  }, [fetchPOs]);

  /* ================= DELETE ================= */
  const deletePO = useCallback(async (id) => {
    try {
      await apiClient.delete(`/api/purchase-orders/${id}`);
      setPurchaseOrders(prev => prev.filter(po => po.id !== id));
      alert('Purchase Order deleted successfully');
    } catch (error) {
      console.error('Delete failed:', error);
      alert('Failed to delete Purchase Order');
    }
  }, []);

  /* ================= FETCH BY ID ================= */
  const fetchPOById = useCallback(async (id) => {
    try {
      const response = await apiClient.get(`/api/purchase-orders/${id}`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch PO:', error);
      return null;
    }
  }, []);

  /* ================= EDIT ================= */
  const onEdit = useCallback(async (po) => {
    const detailedPO = await fetchPOById(po.id);
    if (detailedPO) {
      setSelectedPO(detailedPO);
      setShowCreate(true);
    }
  }, [fetchPOById]);

  /* ================= DOWNLOAD ================= */
  const onDownload = useCallback(async (po) => {
    try {
      const response = await apiClient.get(
        `/api/purchase-orders/${po.id}/download`,
        { responseType: 'blob' }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `PO_${po.po_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Download failed:', error);
      alert('Failed to download PO');
    }
  }, []);

  /* ================= FILTER ================= */
  const getFilteredPOs = () => {
    if (activeTab === 'APPROVED') {
      return purchaseOrders.filter(po => po.status === 'APPROVED');
    }
    if (activeTab === 'PENDING') {
      return purchaseOrders.filter(po => po.status === 'PENDING');
    }
    return purchaseOrders;
  };

  /* ================= UI ================= */
  return (
    <div className="invoice-container">

      {/* Header Row */}
      <div className="header-row">
        <Header title="PURCHASE ORDER" />

        {!showCreate && (
          <Button
            onClick={() => {
              setSelectedPO(null);
              setShowCreate(true);
            }}
            className="create-btn"
          >
            Create Purchase Order
          </Button>
        )}
      </div>

      {!showCreate && (
        <>
          {/* Tabs */}
          <div className="invoice-tab-container">
            <button
              className={`invoice-tab ${activeTab === 'All' ? 'active' : ''}`}
              onClick={() => setActiveTab('All')}
            >
              All
            </button>
            <button
              className={`invoice-tab ${activeTab === 'APPROVED' ? 'active' : ''}`}
              onClick={() => setActiveTab('APPROVED')}
            >
              APPROVED
            </button>
            <button
              className={`invoice-tab ${activeTab === 'PENDING' ? 'active' : ''}`}
              onClick={() => setActiveTab('PENDING')}
            >
              PENDING
            </button>
          </div>

          {/* Table */}
          <PurchaseListTable
            purchaseOrders={getFilteredPOs()}
            refreshOrders={fetchPOs}
            onEdit={onEdit}
            onDelete={(po) => deletePO(po.id)}
            onDownload={onDownload}
            onRowClick={(po) => setSelectedPO(po)}
          />

          {selectedPO && (
            <PoModal
              po={selectedPO}
              onClose={() => setSelectedPO(null)}
            />
          )}
        </>
      )}

      {/* Create / Edit Page */}
      {showCreate && (
        <PoCreate
          invoice={selectedPO}
          isEditable={true}
          onBack={() => {
            setShowCreate(false);
            setSelectedPO(null);
            fetchPOs();
          }}
        />
      )}
    </div>
  );
};

export default PurchaseOrderPage;